import React, { useState, useCallback, useMemo } from "react";
import toast from "react-hot-toast";

const states = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut", "Delaware",
  "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky",
  "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota", "Mississippi",
  "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey", "New Mexico",
  "New York", "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania",
  "Rhode Island", "South Carolina", "South Dakota", "Tennessee", "Texas", "Utah", "Vermont",
  "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming"
];

interface FormData {
  firstName: string;
  lastName: string;
  address: string;
  city: string;
  state: string;
  zipcode: string;
  phone: string;
  email: string;
  dob: string;
  gender: string;
  reminder: boolean;
}

type FormErrors = Partial<Record<keyof FormData, string>>;

type FormSubmitData = Omit<FormData, "phone"> & {
  phone?: string;
};

interface RegistrationCheckFormProps {
  onSubmit?: (data: FormSubmitData) => void;
  isLoading?: boolean;
  initialData?: Partial<FormData>;
  header?: string;
}

export default function RegistrationCheckForm({
  onSubmit,
  isLoading = false,
  initialData = {},
  header = "Check Your Registration Status"
}: RegistrationCheckFormProps) {
  const [form, setForm] = useState<FormData>({
    firstName: "",
    lastName: "",
    address: "",
    city: "",
    state: "",
    zipcode: "",
    phone: "",
    email: "",
    dob: "",
    gender: "",
    reminder: false,
    ...initialData
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  const isFormValid = useMemo(() => {
    return (
      form.firstName &&
      form.lastName &&
      form.address &&
      form.city &&
      form.state &&
      form.zipcode &&
      form.dob &&
      form.gender &&
      form.email
    );
  }, [form]);

  const getAgeFromDob = (dob: string): number => {
    if (!dob) return 0;
    const birthDate = new Date(dob);
    if (Number.isNaN(birthDate.getTime())) return 0;
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const hasBirthdayPassedThisYear =
      today.getMonth() > birthDate.getMonth() ||
      (today.getMonth() === birthDate.getMonth() && today.getDate() >= birthDate.getDate());
    if (!hasBirthdayPassedThisYear) age -= 1;
    return age;
  };

  const validateForm = (): FormErrors => {
    const errors: FormErrors = {};
    if (!form.firstName.trim()) errors.firstName = "First name is required";
    if (!form.lastName.trim()) errors.lastName = "Last name is required";
    if (!form.address.trim()) errors.address = "Home address is required";
    if (!form.city.trim()) errors.city = "City is required";
    if (!form.state.trim()) errors.state = "State is required";
    if (!form.zipcode.trim()) errors.zipcode = "Zipcode is required";
    if (!form.email.trim()) errors.email = "Email address is required";
    if (!form.dob.trim()) {
      errors.dob = "Date of birth is required";
    } else if (getAgeFromDob(form.dob) < 16) {
      errors.dob = "Voter must be 16 years old to be eligible";
    }
    if (!form.gender.trim()) errors.gender = "Gender is required";
    return errors;
  };

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { name, value, type } = e.target;
      setForm((f) => ({
        ...f,
        [name]: type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
      }));
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    },
    []
  );

  const handleGender = useCallback(
    (gender: string) => {
      setForm((f) => ({ ...f, gender }));
      setFormErrors((prev) => ({ ...prev, gender: "" }));
    },
    []
  );

  const handleSubmit = useCallback(async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setFormErrors(validationErrors);
      if (validationErrors.dob === "Voter must be 16 years old to be eligible") {
        toast.error("Voter must be 16 years old to be eligible");
      } else {
        toast.error("Please fill all required fields");
      }
      return;
    }
    setFormErrors({});

    const trimmedPhone = form.phone.trim();
    const payload: FormSubmitData = trimmedPhone
      ? { ...form, phone: trimmedPhone }
      : (({ phone, ...rest }) => rest)(form);

    if (onSubmit) {
      onSubmit(payload);
    }
  }, [form, isFormValid, onSubmit]);

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <h2 className="text-2xl text-gray-900 font-semibold mb-2">{header}</h2>
      {header.includes("Polling") && (<p className="text-gray-600 text-sm mb-6">Enter your information below to look up your Polling location using official state sources.</p>)}
      {header.includes("Registration") && (<p className="text-gray-600 text-sm mb-6">Enter your information below to look up your voter registration using official state sources.</p>)}
      {header.includes("Vote By Mail") && (<p className="text-gray-600 text-sm mb-6">Enter your information below to request for mail ballot using official state sources.</p>)}

      {/* Registration Form */}
      <div className="md:grid grid-cols-1 md:grid-cols-3 space-y-4 md:space-y-0 gap-6 text-black">
        <div>
          <label className="font-semibold">First Name <span className="text-red-600">*</span></label>
          <input
            type="text"
            name="firstName"
            value={form.firstName}
            onChange={handleChange}
            className="w-full border border-gray-400 rounded px-3 py-2 mt-1 transition-all focus:border-red-600"
            placeholder="First Name"
            required
          />
          {formErrors.firstName && <p className="text-red-600 text-xs mt-1">{formErrors.firstName}</p>}
        </div>
        <div>
          <label className="font-semibold">Last Name <span className="text-red-600">*</span></label>
          <input
            type="text"
            name="lastName"
            value={form.lastName}
            onChange={handleChange}
            className="w-full border border-gray-400 rounded px-3 py-2 mt-1 transition-all focus:border-red-600"
            placeholder="Last Name"
            required
          />
          {formErrors.lastName && <p className="text-red-600 text-xs mt-1">{formErrors.lastName}</p>}
        </div>
        <div>
          <label className="font-semibold">State <span className="text-red-600">*</span></label>
          <select
            name="state"
            value={form.state}
            onChange={handleChange}
            className="w-full border border-gray-400 rounded px-3 py-2 mt-1 transition-all focus:border-red-600 bg-white"
            required
          >
            <option value="">Select State</option>
            {states.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          {formErrors.state && <p className="text-red-600 text-xs mt-1">{formErrors.state}</p>}
        </div>
        <div>
          <label className="font-semibold">Zipcode <span className="text-red-600">*</span></label>
          <input
            type="text"
            name="zipcode"
            value={form.zipcode}
            onChange={handleChange}
            className="w-full border border-gray-400 rounded px-3 py-2 mt-1 transition-all focus:border-red-600"
            placeholder="Zipcode"
            required
          />
          {formErrors.zipcode && <p className="text-red-600 text-xs mt-1">{formErrors.zipcode}</p>}
        </div>
        <div>
          <label className="font-semibold">City <span className="text-red-600">*</span></label>
          <input
            type="text"
            name="city"
            value={form.city}
            onChange={handleChange}
            className="w-full border border-gray-400 rounded px-3 py-2 mt-1 transition-all focus:border-red-600"
            placeholder="City"
            required
          />
          {formErrors.city && <p className="text-red-600 text-xs mt-1">{formErrors.city}</p>}
        </div>
        <div>
          <label className="font-semibold">Home Address <span className="text-red-600">*</span></label>
          <input
            type="text"
            name="address"
            value={form.address}
            onChange={handleChange}
            className="w-full border border-gray-400 rounded px-3 py-2 mt-1 transition-all focus:border-red-600"
            placeholder="123 Main St"
            required
          />
          {formErrors.address && <p className="text-red-600 text-xs mt-1">{formErrors.address}</p>}
        </div>
        <div>
          <label className="font-semibold">Phone Number</label>
          <input
            type="text"
            name="phone"
            value={form.phone}
            onChange={handleChange}
            className="w-full border border-gray-400 rounded px-3 py-2 mt-1 transition-all focus:border-red-600"
            placeholder="Phone Number (Optional)"
          />
          {formErrors.phone && <p className="text-red-600 text-xs mt-1">{formErrors.phone}</p>}
        </div>
        <div>
          <label className="font-semibold">Email Address <span className="text-red-600">*</span></label>
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            className="w-full border border-gray-400 rounded px-3 py-2 mt-1 transition-all focus:border-red-600"
            placeholder="Email Address"
            required
          />
          {formErrors.email && <p className="text-red-600 text-xs mt-1">{formErrors.email}</p>}
        </div>
        <div>
          <label className="font-semibold">Date of Birth <span className="text-red-600">*</span></label>
          <input
            type="date"
            name="dob"
            value={form.dob}
            onChange={handleChange}
            className="w-full border border-gray-400 rounded px-3 py-2 mt-1 transition-all focus:border-red-600"
            required
          />
          {formErrors.dob && <p className="text-red-600 text-xs mt-1">{formErrors.dob}</p>}
        </div>
        <div className="flex items-center mt-4 space-x-8 col-span-2">
          <label className="font-semibold">Gender: <span className="text-red-600">*</span></label>
          <label className="flex items-center space-x-2">
            <input type="radio" name="gender" checked={form.gender === "Male"} onChange={() => handleGender("Male")} />
            <span>Male</span>
          </label>
          <label className="flex items-center space-x-2">
            <input type="radio" name="gender" checked={form.gender === "Female"} onChange={() => handleGender("Female")} />
            <span>Female</span>
          </label>
          {formErrors.gender && <p className="text-red-600 text-xs mt-1">{formErrors.gender}</p>}
        </div>
      </div>
      <p className="text-gray-600 text-sm mt-3">Your information is used only to perform this lookup and is not stored as a voter profile</p>

      {/* Election Reminders */}
      <div className="flex flex-col space-y-2 text-black mt-6">
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            name="reminder"
            checked={form.reminder}
            onChange={handleChange}
          />
          <span className="font-semibold">Receive election reminders and action alerts updates from 123-Vote.</span>
        </label>
        <div className="text-xs text-gray-400 mt-1 pl-8 italic">
          By entering your phone number and pressing "Check your registration", you consent to receive periodic text and email about voting information support from 123-Vote. Message and data rates may apply. Text STOP to opt out at any time. You may unsubscribe from email at any time.
        </div>
      </div>

      <div className="flex justify-between mt-8">
        <button
          className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled
        >
          Back
        </button>
        <button
          onClick={handleSubmit}
          disabled={isLoading}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? "Checking..." : "Check Status"}
        </button>
      </div>

      <div className="mt-6 pt-4 border-t border-gray-200">
        <p className="text-xs text-gray-500 text-center">
          Information is sent securely to public election systems or official state websites.
        </p>
      </div>
    </div>
  );
}
